import { Flex, HStack, VStack } from "@chakra-ui/react"
import { useEffect, useState } from "react"
import ConfigureRange, { ConfigureRangeSkeleton } from "@hydrologiq/shared/reporting/ConfigureRange"
import { Granularity } from "@hydrologiq/common/types/reporting/Granularity"
import { nowDateTime, toUserTZ } from "@hydrologiq/common/helpers/date"
import { DateTime } from "timezonecomplete"
import Informative from "@hydrologiq/shared/Informative"
import AssetUsage, { AssetUsageSkeleton, AssetUsageView } from "./AssetUsage"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { getAssignmentEndDate, getAssignmentStartDate, getMonitoredAssignments } from "../../utils/assignmentDates"

import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { FaTableList } from "react-icons/fa6"

function Usage({ asset }: { asset?: gqlTypes.Asset }) {
  const assetName = asset?.name
  const [start, setStart] = useState<DateTime>()
  const [end, setEnd] = useState<DateTime>()

  const [min, setMin] = useState<DateTime>()
  const [max, setMax] = useState<DateTime>()

  const [granularity, setgranularity] = useState<Granularity>(Granularity.Monthly)
  const [view, setView] = useState<AssetUsageView>("table")

  useEffect(() => {
    if (asset?.assignments?.all) {
      // Derive effective date range from monitored assignments (falls back to all).
      // Prefer `range` (self + children rolled up) so a parent's timeline spans
      // its modules' data connections, not just its own.
      const monitoredAssignments = getMonitoredAssignments(asset.assignments.range ?? asset.assignments.all, asset.assignments.monitored)
      const startStr = getAssignmentStartDate(monitoredAssignments)
      const endStr = getAssignmentEndDate(monitoredAssignments)

      const newMin = startStr ? toUserTZ(startStr) : undefined
      const newMax = endStr ? toUserTZ(endStr) : nowDateTime()

      // Only update if values actually changed to prevent re-render loops
      setMin((prev) => (prev?.toUtcIsoString() === newMin?.toUtcIsoString() ? prev : newMin))
      setMax((prev) => (prev?.toUtcIsoString() === newMax?.toUtcIsoString() ? prev : newMax))
    } else {
      setMin(undefined)
      setMax(undefined)
      setStart(undefined)
      setEnd(undefined)
    }
  }, [asset?.id, asset?.assignments?.all?.length])

  return !asset?.assignments || !min ? (
    <Informative
      heading="Waiting for data to be available"
      text="Usage information will be available when an asset has been utilised."
    />
  ) : (
    <>
      <VStack align="stretch">
        <HStack
          gap={5}
          height="100%"
          justify="flex-start"
          align="flex-end"
          flexDirection={{ base: "column", lg: "row" }}
        >
          <VStack align="stretch" width={{ base: "100%", lg: "50%" }}>
            {!min || !max ? (
              <ConfigureRangeSkeleton />
            ) : (
              <ConfigureRange
                min={min}
                max={max}
                callback={(granularity, start, end) => {
                  setgranularity(granularity)
                  setStart(start)
                  setEnd(end)
                }}
                granularityDefault={Granularity.Daily}
              />
            )}
          </VStack>
          <Flex height="100%">
            <SegmentedControl
              variant="hydrologiq_icon"
              items={[
                { value: "table", label: "Table", icon: FaTableList },
                // Chart view removed as requested (no graphing)
              ]}
              onValueChange={(value) => setView(value.value as AssetUsageView)}
              value={view}
            />
          </Flex>
        </HStack>

        {!asset || !assetName || !end || !start ? (
          <AssetUsageSkeleton view={view} />
        ) : (
          <AssetUsage
            assetId={asset.id}
            end={end}
            start={start}
            granularity={granularity}
            assetName={assetName}
            view={view}
          />
        )}
      </VStack>
    </>
  )
}

export default Usage
